import i18n from 'infrastructure/i18n';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard';
import useUser from 'utils/useUser';
import classNames from 'classnames';
import { Flex } from 'antd';

import s from './styles.module.scss';
import ReadingsColumn from './readings';
import PatientCell from './patient';
import LatestReading from './latest-reading';
import AssignCell from './assign';
import ExpandContainer from '../expand-container';
import Averages from '../averages';
import PatientInfo from '../patient-info';
import InteractiveTime from '../interactive-time';
import ReadingTable from '../reading-table';
import {
  columnsWidth,
  POD_SECTION_CONFIG,
  ROW_BORDER_WIDTH,
  SLA_URGENT_BORDER_COLOR,
  SLA_AVATAR_BG,
  SLA_AVATAR_TEXT_COLOR,
} from '../../constants';

import type { RpmOverviewPatient } from 'infrastructure/hooks/rpm/overview-dashboard/use-rpm-overview-dashboard';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';

export const useRpmOverviewTableColumns = (
  isLoadingReadings = false,
  isUrgentSection = false,
) => {
  const { t } = useTranslation();
  const [expandIndexes, setExpandIndexes] = useState<number[]>([]);
  const {
    filter: { endTime, startTime },
  } = useOverviewDashboardFilter();
  const {
    isMobileDevice,
    isCriticalOrAtRiskAlertingRole,
    isCriticalRole,
    isAtRiskRole,
    isAgencyDoctor,
  } = useUser();

  const hideShowAssignCell = isCriticalOrAtRiskAlertingRole || !isAgencyDoctor;

  let readingCellWidth = columnsWidth.readingType;
  if (isCriticalRole) {
    readingCellWidth = columnsWidth.readingType / 3;
  } else if (isAtRiskRole) {
    readingCellWidth = (columnsWidth.readingType / 3) * 2;
  }

  const columns: IBaseTableColumns<RpmOverviewPatient> = [
    {
      label: i18n.t('labels.patient'),
      key: 'patient',
      onCell: () => ({
        colSpan: 3,
      }),
      render: (_, record, idx) => {
        const isExpanded = expandIndexes.includes(idx);

        const onExpand = () => {
          if (isExpanded) {
            setExpandIndexes(expandIndexes.filter((el) => el !== idx));
          } else {
            setExpandIndexes([...expandIndexes, idx]);
          }
        };

        const podConfig = POD_SECTION_CONFIG[record.podSection ?? 'none'];
        const borderColor = isUrgentSection
          ? SLA_URGENT_BORDER_COLOR
          : podConfig.borderColor;
        // SLA section: all avatars use the same red regardless of pod tier
        const avatarOverride = isUrgentSection
          ? { bg: SLA_AVATAR_BG, color: SLA_AVATAR_TEXT_COLOR }
          : { bg: podConfig.avatarBg, color: podConfig.avatarTextColor };

        return (
          <div
            className={s['column-wrapper']}
            style={
              record.podSection != null || isUrgentSection
                ? { borderLeft: `${ROW_BORDER_WIDTH} solid ${borderColor}` }
                : undefined
            }
          >
            <div
              className={classNames(s['row-wrapper'], {
                [s['row-wrapper--disabled']]: isCriticalOrAtRiskAlertingRole,
              })}
            >
              <Flex gap={20}>
                <PatientCell
                  guid={record.guid}
                  dob={record.dob}
                  name={record.name}
                  language={record.language}
                  provider={record.provider}
                  onExpand={onExpand}
                  expand={isExpanded}
                  pmpmStatus={record.pmpmStatus}
                  interactionStatus={record.interactionStatus}
                  rpmStatus={record.latestReading?.rpmStatus}
                  ccmStatus={record.latestReading?.ccmStatus}
                  isLoadingProgramType={isLoadingReadings}
                  doNotCallRestrictionType={record.doNotCallRestrictionType}
                  doNotCallStartTime={record.doNotCallStartTime}
                  doNotCallEndTime={record.doNotCallEndTime}
                  clinicDoNotCallRestrictionType={
                    record.clinicDoNotCallRestrictionType
                  }
                  clinicDoNotCallStartTime={record.clinicDoNotCallStartTime}
                  clinicDoNotCallEndTime={record.clinicDoNotCallEndTime}
                  timezone={record.timezone}
                  podSection={record.podSection}
                  podName={record.podName}
                  podInfoTextColor={
                    record.podSection != null ? podConfig.textColor : undefined
                  }
                  colorOverride={
                    record.podSection != null || isUrgentSection
                      ? avatarOverride
                      : undefined
                  }
                />
                <LatestReading
                  id={record.latestReading?.patientGuid}
                  values={record.latestReading?.value}
                  statuses={record.latestReading?.statuses}
                  createdAt={record.latestReading?.createdAt}
                  isLoading={isLoadingReadings}
                  isDisplayTimestampVertically={false}
                />
              </Flex>
              <Flex gap={20}>
                <ReadingsColumn
                  critical={record.readings?.critical}
                  atRisk={record.readings?.atRisk}
                  normal={record.readings?.normal}
                  disableRiskAndNormal={record?.overTime}
                />
                {!hideShowAssignCell && (
                  <AssignCell
                    patientGuid={record.guid}
                    assignee={record.assignee}
                  />
                )}
              </Flex>
            </div>
            {isExpanded && (
              <ExpandContainer
                patientGuid={record.guid}
                endTime={endTime}
                startTime={startTime}
                average={
                  !isCriticalOrAtRiskAlertingRole && (
                    <Averages patientGuid={record.guid} endTime={endTime} />
                  )
                }
                info={
                  !isCriticalOrAtRiskAlertingRole && (
                    <Flex
                      vertical
                      justify="space-between"
                      style={{ height: '100%' }}
                    >
                      <InteractiveTime patientGuid={record.guid} />
                      <PatientInfo patientGuid={record.guid} />
                    </Flex>
                  )
                }
                table={({ page, setPage, activity, loading }) => (
                  <ReadingTable
                    activity={activity}
                    isLoading={loading}
                    page={page}
                    setPage={setPage}
                    name={t('labels.comments')}
                  />
                )}
              />
            )}
          </div>
        );
      },
    },
    {
      label: i18n.t('labels.reading'),
      key: 'readingType',
      width: `${readingCellWidth}px`,
      onCell: () => ({
        colSpan: 0,
      }),
    },
  ];

  if (!hideShowAssignCell) {
    columns.push({
      label: i18n.t('labels.assign'),
      key: 'comments',
      width: `${columnsWidth.assign}px`,
      onCell: () => ({
        colSpan: 0,
      }),
    });
  }

  const mobileColumns: IBaseTableColumns<RpmOverviewPatient> = [
    {
      label: i18n.t('labels.patient'),
      key: 'patient',
      render: (_, record, idx) => {
        const isExpanded = expandIndexes.includes(idx);

        const onExpand = () => {
          if (isExpanded) {
            setExpandIndexes(expandIndexes.filter((el) => el !== idx));
          } else {
            setExpandIndexes([...expandIndexes, idx]);
          }
        };
        const mobilePodConfig = POD_SECTION_CONFIG[record.podSection ?? 'none'];
        const mobileAvatarOverride = isUrgentSection
          ? { bg: SLA_AVATAR_BG, color: SLA_AVATAR_TEXT_COLOR }
          : {
              bg: mobilePodConfig.avatarBg,
              color: mobilePodConfig.avatarTextColor,
            };

        return (
          <div className={s['mobile-column-wrapper']}>
            <div
              className={classNames(s['mobile-row-wrapper'], {
                [s['mobile-row-wrapper--disabled']]:
                  isCriticalOrAtRiskAlertingRole,
              })}
            >
              <PatientCell
                guid={record.guid}
                dob={record.dob}
                name={record.name}
                language={record.language}
                provider={record.provider}
                onExpand={onExpand}
                expand={isExpanded}
                pmpmStatus={record.pmpmStatus}
                rpmStatus={record.latestReading?.rpmStatus}
                ccmStatus={record.latestReading?.ccmStatus}
                interactionStatus={record.interactionStatus}
                isLoadingProgramType={isLoadingReadings}
                podSection={record.podSection}
                podName={record.podName}
                podInfoTextColor={
                  record.podSection != null
                    ? mobilePodConfig.textColor
                    : undefined
                }
                colorOverride={
                  record.podSection != null || isUrgentSection
                    ? mobileAvatarOverride
                    : undefined
                }
              />
              <div
                className={classNames(s['mobile-readings-wrapper'], {
                  [s['two-rows']]: isCriticalOrAtRiskAlertingRole,
                })}
              >
                <ReadingsColumn
                  critical={record.readings?.critical}
                  atRisk={record.readings?.atRisk}
                  normal={record.readings?.normal}
                  disableRiskAndNormal={record?.overTime}
                />
                {!hideShowAssignCell && (
                  <AssignCell
                    patientGuid={record.guid}
                    assignee={record.assignee}
                  />
                )}
              </div>
            </div>
            {isExpanded && (
              <ExpandContainer
                patientGuid={record.guid}
                endTime={endTime}
                startTime={startTime}
                average={
                  !isCriticalOrAtRiskAlertingRole && (
                    <Averages patientGuid={record.guid} endTime={endTime} />
                  )
                }
                info={
                  !isCriticalOrAtRiskAlertingRole && (
                    <PatientInfo patientGuid={record.guid} />
                  )
                }
                table={({ page, setPage, activity, loading }) => (
                  <ReadingTable
                    activity={activity}
                    isLoading={loading}
                    page={page}
                    setPage={setPage}
                    name={t('labels.comments')}
                  />
                )}
              />
            )}
          </div>
        );
      },
    },
  ];

  return isMobileDevice ? mobileColumns : columns;
};
